/**
 * Convert an image URL to Base64 (without data:image/... prefix)
 * @param url Image URL
 */
import * as yup from "yup";
import { Path } from "react-hook-form";
import { t } from "i18next";
import CryptoJS from 'crypto-js';
import { PACKAGES } from "../../data/pricing";

// BUILD PHONE CONTACT TYPES START
export interface SocialItem {
  platform: string;
  url: string;
  enabled: boolean;
}

export interface ContactInfo {
  firstName: string;
  lastName: string;
  organization: string;
  phone: string;
  email: string;
  website: string;
  socials?: { label: string; url: string }[];
  street?: string;
  addressLabel?: string;
  photoBase64?: string;
  photoType?: string;
}

interface DateFormatOptions {
  locale?: string;
  timezone?: string;
  dateStyle?: 'full' | 'long' | 'medium' | 'short';
  timeStyle?: 'full' | 'long' | 'medium' | 'short';
  separator?: string;
  includeTime?: boolean;
  format?: 'date-only' | 'time-only' | 'datetime';
}

interface BuildContactParams {
  firstName?: string;
  lastName?: string;
  userCompany?: string;
  phone?: string | number;
  email?: string;
  website?: string;
  address?: string;
  socials?: string; // JSON string
  logoPath?: string;
}
// BUILD PHONE CONTACT TYPES END

// TRANSLATABLE FIELDS START
export const BASE_FIELDS = [
  "name",
  "lastname",
  "specialist",
  "description",
  "company",
  "address",
] as const;

// TRANSLATABLE FIELDS START FOR SERVICES
export const SERVICES_FIELDS = [
  "title",
  "description",
  "servicesButtonText",
  "servicesOrderButtonText",
] as const;

type BaseField = typeof BASE_FIELDS[number];
export type FormValues = yup.InferType<ReturnType<typeof buildSchema>>;
export interface LangConfig {
  code: string;
  show: boolean;
}
// TRANSLATABLE FIELDS END

const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];


export async function imageUrlToBase64(
  url: string
): Promise<{ base64: string; type: "JPEG" | "PNG" }> {
  // Use fetch, but note: external servers need CORS headers.
  // If CORS fails, you must proxy the image through your backend.
  const response = await fetch(url, { mode: "cors" });
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.statusText}`);
  }

  const blob = await response.blob();
  const type = blob.type.includes("png") ? "PNG" : "JPEG";

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Strip "data:image/jpeg;base64," prefix
      const base64 = result.split(",")[1];
      resolve({ base64, type });
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export function capitalizeFirstLetter(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export function lightenColor(hex: string, percent: number): string {
  if (!hex) return hex;

  let num = parseInt(hex.replace('#', ''), 16);
  let amt = Math.round(2.55 * percent);

  let R = (num >> 16) + amt;
  let G = (num >> 8 & 0x00FF) + amt;
  let B = (num & 0x0000FF) + amt;

  return '#' + (
    0x1000000 +
    (R < 255 ? (R < 0 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 0 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 0 ? 0 : B) : 255)
  ).toString(16).slice(1).toUpperCase();
}

// Dynamic generator
export function generateFields(base: BaseField, langs: LangConfig[]) {
  return langs.map((lang) => {
    const key = lang.code === "am" ? base : `${base}${lang.code}`;
    return {
      key: key as Path<FormValues>,
      label: t(`order-form.${base}-${lang.code}`),
      show: lang.show,
    };
  });
}


export function transformDataToObject(formData: any, langs: { code: string; show: boolean }[]) {
  const result: any = {
    translations: [] as Record<string, boolean>[],
  };

  // Initialize base fields dynamically
  BASE_FIELDS.forEach((base) => {
    result[base] = {};
  });

  langs.forEach(({ code, show }) => {
    BASE_FIELDS.forEach((base) => {
      const key = code === "am" ? base : `${base}${code}`;
      result[base][code] = formData[key] || "";
    });

    // translations flags
    result.translations.push({ [code]: show }); // 👈 as string
  });

  return result;
}

export function buildSchema(langs: LangConfig[]) {
  const shape: Record<string, yup.StringSchema> = {};

  BASE_FIELDS.forEach((base) => {
    langs.forEach((lang) => {
      const key = lang.code === "am" ? base : `${base}${lang.code}`;
      shape[key] = yup.string();
    });
  });

  return yup.object().shape({
    ...shape,
    location: yup.string(),
    colorScheme: yup.string(),
    wdays: yup
      .array()
      .of(yup.string().oneOf(["mon", "tue", "wed", "thu", "fri", "sat", "sun"])),
    whours: yup.string(),
    website: yup.string(),
    wifi: yup.string(),
    feedback: yup.boolean(),
    email: yup.string().email(t("order-form.email-invalid")),
    phone: yup
      .string()
      .nullable()
      .notRequired()
      .when([], {
        is: (value: string | null | undefined) => !!value && value.trim() !== "",
        then: (schema) =>
          schema
            .matches(/^[0-9()+-\s]+$/, t("order-form.phone-invalid"))
            .min(7, t("order-form.phone-invalid"))
            .max(15, t("order-form.phone-invalid")),
      }),
    additionalPhones: yup.array().of(
      yup.object().shape({
        value: yup
          .string()
          .matches(/^[0-9()+-\s]+$/, t("order-form.phone-invalid"))
          .min(7, t("order-form.phone-invalid"))
          .max(15, t("order-form.phone-invalid")),
      })
    ),
    additionalEmails: yup.array().of(
      yup.object().shape({
        value: yup.string().email(t("order-form.email-invalid")),
      })
    ),

    template: yup.string(),
    image: yup.mixed().nullable(),
    coverImage: yup.mixed().nullable(),
    documents: yup.mixed().nullable(),
    title: yup.string(),
    cardType: yup.string(),
    type: yup.string(),
    status: yup.string(),

    is_armenian_on: yup.string(),
    is_russian_on: yup.string().nullable(),
    is_english_on: yup.string().nullable(),

    is_instagram_on: yup.string().nullable(),
    is_facebook_on: yup.string().nullable(),
    is_linkedin_on: yup.string().nullable(),
    is_whatsapp_on: yup.string().nullable(),
    is_telegram_on: yup.string().nullable(),
    is_tiktok_on: yup.string().nullable(),
    is_youtube_on: yup.string().nullable(),
    is_viber_on: yup.string().nullable(),
    is_tripadvisor_on: yup.string().nullable(),
    is_booking_on: yup.string().nullable(),

    instagram: yup.string().nullable(),
    facebook: yup.string().nullable(),
    linkedin: yup.string().nullable(),
    whatsapp: yup.string().nullable(),
    telegram: yup.string().nullable(),
    tiktok: yup.string().nullable(),
    youtube: yup.string().nullable(),
    viber: yup.string().nullable(),
    tripadvisor: yup.string().nullable(),
    booking: yup.string().nullable(),
  });
}

export function buildServicesSchema(langs: LangConfig[]) {
  const serviceSchema = yup.object().shape({
    titleam: yup.string().required(t('form-messages.required')),
    titleen: langs.find(l => l.code === 'en' && l.show)
      ? yup.string().required(t('form-messages.required'))
      : yup.string(),
    titleru: langs.find(l => l.code === 'ru' && l.show)
      ? yup.string().required(t('form-messages.required'))
      : yup.string(),
    descriptionam: yup.string(),
    descriptionen: yup.string(),
    descriptionru: yup.string(),
    price: yup.string(),
    active: yup.boolean().default(true),
    image: yup.mixed(), // Can be File or string (URL)
    position: yup.number().default(0),
    _id: yup.string(), // For existing services
  });

  return yup.object().shape({
    servicesEnabled: yup.boolean().default(false),
    servicesButtonText: yup.string().when('servicesEnabled', {
      is: true,
      then: (schema) => schema.required(t('form-messages.required')),
      otherwise: (schema) => schema,
    }),
    servicesButtonTexten: langs.find(l => l.code === 'en' && l.show)
      ? yup.string().when('servicesEnabled', {
        is: true,
        then: (schema) => schema.required(t('form-messages.required')),
        otherwise: (schema) => schema,
      })
      : yup.string(),
    servicesButtonTextru: langs.find(l => l.code === 'ru' && l.show)
      ? yup.string().when('servicesEnabled', {
        is: true,
        then: (schema) => schema.required(t('form-messages.required')),
        otherwise: (schema) => schema,
      })
      : yup.string(),
    servicesOrderButtonText: yup.string(),
    servicesOrderButtonTexten: yup.string(),
    servicesOrderButtonTextru: yup.string(),
    services: yup.array().of(serviceSchema).default([]),
  });
}


export function transformServicesDataToObject(formData: any, langs: { code: string; show: boolean }[]) {
  const result: any = {
    translations: [] as Record<string, boolean>[],
  };

  // Initialize base fields dynamically
  SERVICES_FIELDS.forEach((base) => {
    result[base] = {};
  });

  langs.forEach(({ code, show }) => {
    SERVICES_FIELDS.forEach((base) => {
      const key = code === "am" ? base : `${base}${code}`;
      result[base][code] = formData[key] || "";
    });

    // translations flags
    result.translations.push({ [code]: show }); // 👈 as string
  });

  return result;
}

type LangRecord = Record<string, boolean>;

export function getFirstTrueLang(languages: LangRecord[]): string | null {
  for (const langObj of languages) {
    const [lang, isTrue] = Object.entries(langObj)[0];
    if (isTrue) return lang;
  }
  return null;
};

export async function buildContact({
  firstName = "",
  lastName = "",
  userCompany = "",
  phone = "",
  email = "",
  website = "",
  address = "",
  socials,
  logoPath,
}: BuildContactParams): Promise<ContactInfo> {
  const contactData: ContactInfo = {
    firstName,
    lastName,
    organization: userCompany,
    phone: phone ? String(phone) : "",
    email,
    website,
    // The card stores one free-text address, so it goes in the vCard's
    // street component; buildVCard omits ADR entirely when it's empty.
    street: address.trim(),
  };

  if (socials) {
    try {
      const contactSocials = JSON.parse(socials)
        .filter((social: SocialItem) => social.enabled && social.url)
        .map((social: SocialItem) => {
          let socialUrl: string;

          switch (social.platform) {
            case "whatsapp":
              socialUrl = `https://wa.me/${social.url}`;
              break;
            case "telegram":
              socialUrl = `tg://resolve?domain=${social.url}`;
              break;
            case "viber":
              socialUrl = `viber://chat?number=${social.url}`;
              break;
            default:
              socialUrl = social.url;
          }

          return {
            label: capitalizeFirstLetter(social.platform),
            url: socialUrl,
          };
        });

      if (contactSocials.length > 0) {
        contactData.socials = contactSocials;
      }
    } catch (err) {
      console.warn("Invalid socials JSON:", err);
    }
  }

  if (logoPath) {
    try {
      const { base64, type } = await imageUrlToBase64(logoPath);
      contactData.photoBase64 = base64;
      contactData.photoType = type;
    } catch (err) {
      console.warn("Failed to load image for vCard:", err);
    }
  }

  return contactData;
}

// Function to format the date as 'DD-MM-YYYY'
export function formatDate(date: string) {
  return new Date(date).toLocaleDateString('en-GB').replace(/\//g, '-');
}

// Date + 24h time, e.g. "14-07-2026 09:18"
export function formatDateTime(date: string) {
  const d = new Date(date);
  const datePart = d.toLocaleDateString('en-GB').replace(/\//g, '-');
  const timePart = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${datePart} ${timePart}`;
}

export function detectDeviceType(userAgent: string): "mobile" | "tablet" | "desktop" {
  const ua = userAgent.toLowerCase();

  if (/mobile|iphone|ipod|android.*mobile|windows phone|blackberry/.test(ua)) {
    return "mobile";
  }

  if (/ipad|tablet|android(?!.*mobile)/.test(ua)) {
    return "tablet";
  }

  return "desktop";
}

/**
 * Decrypts data encrypted with AES-256-CBC using the backend DATA_SECRET_KEY
 * @param encryptedString - Format: ivBase64:encryptedBase64
 * @returns Original object
 */
export function decryptData(encryptedString: string): any {
  if (!process.env.REACT_APP_DATA_SECRET_KEY) {
    throw new Error('Missing key');
  }

  const key = CryptoJS.enc.Base64.parse(process.env.REACT_APP_DATA_SECRET_KEY);

  const [ivBase64, encryptedBase64] = encryptedString.split(':');
  if (!ivBase64 || !encryptedBase64) {
    throw new Error('Invalid encrypted string');
  }

  const iv = CryptoJS.enc.Base64.parse(ivBase64);

  const decrypted = CryptoJS.AES.decrypt(encryptedBase64, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);

  if (!decryptedText) {
    throw new Error('Malformed UTF-8 data. Check key/iv/ciphertext.');
  }

  try {
    return JSON.parse(decryptedText);
  } catch (parseError) {
    throw new Error(`JSON parse failed: ${parseError}`);
  }
}

export const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export function formatDateAdvanced(
  date: string | Date,
  options: DateFormatOptions = {}
): string {
  const {
    locale = 'en-GB',
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
    dateStyle = 'short',
    timeStyle = 'short',
    separator = '-',
    includeTime = true,
    format = 'datetime'
  } = options;

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Create formatter options
  const formatOptions: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
  };

  // Add date/time style based on format
  if (format === 'date-only' || format === 'datetime') {
    formatOptions.dateStyle = dateStyle;
  }
  if ((format === 'time-only' || format === 'datetime') && includeTime) {
    formatOptions.timeStyle = timeStyle;
  }

  const formatted = new Intl.DateTimeFormat(locale, formatOptions).format(dateObj);

  // Replace slashes with custom separator for date portion
  return formatted.replace(/\//g, separator);
}


export function formatWorkingDays(days: string[]): string {
  if (!Array.isArray(days) || days.length === 0) return "";

  const sorted = [...days].sort(
    (a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b)
  );

  const ranges: string[] = [];
  let start = sorted[0];
  let prev = sorted[0];

  for (let i = 1; i <= sorted.length; i++) {
    const current = sorted[i];
    const prevIndex = DAY_ORDER.indexOf(prev);
    const currentIndex = DAY_ORDER.indexOf(current);

    // not consecutive or at end
    if (i === sorted.length || currentIndex !== prevIndex + 1) {
      if (start === prev) ranges.push(t(`days-short.${start}`));
      else ranges.push(`${t(`days-short.${start}`)}-${t(`days-short.${prev}`)}`);
      start = current;
    }
    prev = current;
  }

  return ranges.join(", ");
}

export function getSimpleFingerprint() {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) return null;

  // Canvas fingerprinting
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#f60';
  ctx.fillRect(125, 1, 62, 20);
  ctx.fillStyle = '#069';
  ctx.fillText('Browser Fingerprint', 2, 15);
  ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
  ctx.fillText('Browser Fingerprint', 4, 17);

  const canvasData = canvas.toDataURL();

  // Combine various browser properties
  const screenInfo = window.screen; // Access screen through window object
  const fingerprint = {
    canvas: canvasData,
    screen: `${screenInfo.width}x${screenInfo.height}x${screenInfo.colorDepth}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    platform: navigator.platform,
    userAgent: navigator.userAgent,
    hardwareConcurrency: navigator.hardwareConcurrency,
    deviceMemory: (navigator as any).deviceMemory || 'unknown',
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
  };

  // Simple hash
  const str = JSON.stringify(fingerprint);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }

  return hash.toString(36);
}

export function formatNumber(num: number) {
  return new Intl.NumberFormat('en-US').format(num);
}

export const getPriceByType = (type: string): number | null => {
  const pkg = PACKAGES.find(p => p.type === type);
  return pkg ? pkg.price : null;
};
